// GoodReceiptPDF.js

import React from "react";

import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

// ======================================
// STYLES
// ======================================
const styles = StyleSheet.create({
  page: {
    paddingTop: 35,
    paddingHorizontal: 30,
    paddingBottom: 60,
    fontSize: 10,
    fontFamily: "Helvetica",
    backgroundColor: "#fff",
  },

  discountText: {
    color: "#f03717",
    fontWeight: "bold",
  },

  header: {
    borderBottom: "2 solid #804a05",
    paddingBottom: 12,
    marginBottom: 20,
  },

  title: {
    fontSize: 22,
    fontWeight: "bold",
    color: "#E11D48",
    marginBottom: 5,
  },

  subTitle: {
    fontSize: 10,
    marginBottom: 2,
    color: "#444",
  },

  receiptTitle: {
    marginTop: 12,
    fontSize: 14,
    fontWeight: "bold",
  },

  statusBox: {
    marginBottom: 20,
    padding: 10,
    backgroundColor: "#F3F4F6",
    borderRadius: 4,
  },

  statusText: {
    fontSize: 11,
    fontWeight: "bold",
  },

  bodyContainer: {
    flexDirection: "row",
    gap: 20,
  },

  imageContainer: {
    width: "35%",
  },

  goodsImage: {
    width: "100%",
    height: 180,
    objectFit: "cover",
    border: "1 solid #ccc",
    borderRadius: 6,
  },

  noImage: {
    height: 180,
    border: "1 solid #ccc",
    justifyContent: "center",
    alignItems: "center",
    fontSize: 12,
  },

  infoContainer: {
    width: "65%",
  },

  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 8,
    color: "#111827",
  },

  row: {
    flexDirection: "row",
    marginBottom: 7,
  },

  label: {
    width: 130,
    fontWeight: "bold",
    color: "#374151",
  },

  value: {
    flex: 1,
  },

  paymentBox: {
    marginTop: 25,
    border: "1 solid #111827",
  },

  paymentHeader: {
    backgroundColor: "#111827",
    color: "#fff",
    padding: 8,
    fontSize: 11,
    fontWeight: "bold",
  },

  paymentRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottom: "1 solid #ddd",
  },

  paymentLabel: {
    fontWeight: "bold",
  },

  balanceText: {
    color: "#DC2626",
    fontWeight: "bold",
  },

  totalPaidText: {
    color: "#2563EB",
    fontWeight: "bold",
  },

  footer: {
    position: "absolute",
    bottom: 20,
    left: 30,
    right: 30,
    borderTop: "1 solid #ddd",
    paddingTop: 10,
    textAlign: "center",
    fontSize: 9,
    color: "#666",
  },

  pageNumber: {
    marginTop: 5,
  },
});

// ======================================
// DOCUMENT
// ======================================
const GoodReceiptPDF = ({
  shipment,
  paymentAmount,
  paymentLocation,
}) => {
  if (!shipment) return null;

  // ✅ FIX: Build absolute image URL for production
  const API_BASE_URL =
    process.env.REACT_APP_API_URL ||
    "https://zuluwhiskeyshipping.online";

  const goodsImageUrl = shipment.goods_image
    ? shipment.goods_image.startsWith("http")
      ? shipment.goods_image
      : `${API_BASE_URL}${shipment.goods_image}`
    : null;

  const amountCharge = Number(
    shipment.amount_charge || 0
  );

  const discount = Number(
    shipment.discount || 0
  );

  const netCharge = amountCharge - discount;

  const totalPaid = Number(
    shipment.amount_paid || 0
  );

  const currentPayment = Number(
    paymentAmount || 0
  );

  const balance = Number(
    shipment.balance || 0
  );

  const previousPaid =
    totalPaid - currentPayment;

  const currentPaymentRecord =
    shipment.payments?.length
      ? shipment.payments[
          shipment.payments.length - 1
        ]
      : null;

  const currentPaymentDate =
    currentPaymentRecord?.created_at
      ? new Date(
          currentPaymentRecord.created_at
        ).toLocaleString()
      : "N/A";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* HEADER */}
        <View style={styles.header}>
          <Text style={styles.title}>
            Zuluwhiskey Shipping
          </Text>

          <Text style={styles.subTitle}>
            Johnsonville, Pipeline Kpelleh Town Road, Monrovia, Liberia
          </Text>

          <Text style={styles.subTitle}>
            Email: info@zuluwhiskeyshipping.com
          </Text>

          <Text style={styles.subTitle}>
            Contact: 267-474-7602, 4842880201, +231-773-275-388, +231-889-239-865
          </Text>

          <Text style={styles.receiptTitle}>
            GOODS PAYMENT RECEIPT
          </Text>
        </View>

        {/* STATUS */}
        <View style={styles.statusBox}>
          <Text style={styles.statusText}>
            Payment Received Successfully
          </Text>

          <Text>
            Payment Location:{" "}
            {paymentLocation === "us"
              ? "United States"
              : "Liberia"}
          </Text>

          <Text>
            Current Payment Date:{" "}
            {currentPaymentDate}
          </Text>
        </View>

        {/* BODY */}
        <View style={styles.bodyContainer}>
          <View style={styles.imageContainer}>
            {goodsImageUrl ? (
              <Image
                src={goodsImageUrl}
                style={styles.goodsImage}
              />
            ) : (
              <View style={styles.noImage}>
                <Text>No Goods Image</Text>
              </View>
            )}
          </View>

          <View style={styles.infoContainer}>
            <Text style={styles.sectionTitle}>
              Shipment Details
            </Text>

            <View style={styles.row}>
              <Text style={styles.label}>
                Customer:
              </Text>
              <Text style={styles.value}>
                {shipment.customer_name}
              </Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>
                Email:
              </Text>
              <Text style={styles.value}>
                {shipment.email}
              </Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>
                Tracking Code:
              </Text>
              <Text style={styles.value}>
                {shipment.tracking_code || "N/A"}
              </Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>
                Items:
              </Text>
              <Text style={styles.value}>
                {shipment.items_name}
              </Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>
                Receiver:
              </Text>
              <Text style={styles.value}>
                {shipment.receiver_name}
              </Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>
                Contact:
              </Text>
              <Text style={styles.value}>
                {shipment.receiver_contact}
              </Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>
                Address:
              </Text>
              <Text style={styles.value}>
                {shipment.receiver_address}
              </Text>
            </View>
          </View>
        </View>

        {/* PAYMENT SUMMARY */}
        <View style={styles.paymentBox}>
          <Text style={styles.paymentHeader}>
            PAYMENT SUMMARY
          </Text>

          <View style={styles.paymentRow}>
            <Text style={styles.paymentLabel}>
              Amount Charge
            </Text>
            <Text>
              ${amountCharge.toFixed(2)}
            </Text>
          </View>

          <View style={styles.paymentRow}>
            <Text style={styles.discountText}>
              Discount
            </Text>
            <Text style={styles.discountText}>
              -${discount.toFixed(2)}
            </Text>
          </View>

          <View style={styles.paymentRow}>
            <Text style={styles.paymentLabel}>
              Net Charge
            </Text>
            <Text>
              ${netCharge.toFixed(2)}
            </Text>
          </View>

          <View style={styles.paymentRow}>
            <Text style={styles.paymentLabel}>
              Previous Paid
            </Text>
            <Text>
              ${previousPaid.toFixed(2)}
            </Text>
          </View>

          <View style={styles.paymentRow}>
            <Text style={styles.paymentLabel}>
              This Payment
            </Text>
            <Text>
              ${currentPayment.toFixed(2)}
            </Text>
          </View>

          <View style={styles.paymentRow}>
            <Text style={styles.totalPaidText}>
              Total Paid
            </Text>
            <Text style={styles.totalPaidText}>
              ${totalPaid.toFixed(2)}
            </Text>
          </View>

          <View
            style={[
              styles.paymentRow,
              { borderBottom: 0 },
            ]}
          >
            <Text style={styles.balanceText}>
              Remaining Balance
            </Text>
            <Text style={styles.balanceText}>
              ${balance.toFixed(2)}
            </Text>
          </View>
        </View>

        {/* FOOTER */}
        <View style={styles.footer} fixed>
          <Text>
            Thank you for choosing Zuluwhiskey Shipping.
          </Text>

          <Text>
            This receipt serves as proof of payment.
          </Text>

          <Text
            style={styles.pageNumber}
            render={({
              pageNumber,
              totalPages,
            }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

export default GoodReceiptPDF;

